import { useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Instagram, Twitter, Copy, Check } from "lucide-react";

const ROLES = [
  { key: "parent", label: "Parent / Caregiver" },
  { key: "grandparent", label: "Grandparent or guardian" },
  { key: "therapist", label: "Therapist / Counselor" },
  { key: "teacher", label: "Teacher / School counselor" },
  { key: "advocate", label: "Guardian ad litem / Social worker / Family advocate" },
  { key: "other", label: "Other" },
];

function ShareRow() {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.origin : "https://lovewellproject.com";
  const shareText = encodeURIComponent(
    "Some people love well. Others love differently. Love Well is a new picture-book series for parents, teachers, and counselors helping children through hard chapters. Join the waitlist:"
  );
  const xShare = `https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(url)}`;
  const igShare = "https://www.instagram.com/";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  return (
    <div className="mt-10 pt-8 border-t border-black/10">
      <div className="mono text-[10px] tracking-[0.28em] uppercase text-[#4A7C7B] mb-4">
        Know someone else who might need this?
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <a
          data-testid="share-instagram"
          href={igShare}
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-full border border-black/10 px-4 py-2.5 text-sm hover:bg-[#2C241B] hover:text-[#FAF8F5] transition-colors"
        >
          <Instagram size={16} /> Share on Instagram
        </a>
        <a
          data-testid="share-x"
          href={xShare}
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-full border border-black/10 px-4 py-2.5 text-sm hover:bg-[#2C241B] hover:text-[#FAF8F5] transition-colors"
        >
          <Twitter size={16} /> Share on X
        </a>
        <button
          data-testid="share-copy"
          onClick={copy}
          className="inline-flex items-center gap-2 rounded-full border border-black/10 px-4 py-2.5 text-sm hover:bg-[#2C241B] hover:text-[#FAF8F5] transition-colors"
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>
    </div>
  );
}

export default function WaitlistForm({ compact = false, source = "direct" }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [role, setRole] = useState("parent");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email,
        first_name: firstName || null,
        role,
        source,
      });
      setDone(true);
      toast.success("You're on the list. A short welcome from Charlie is on its way.");
    } catch (err) {
      toast.error("Something went sideways. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <motion.div
        data-testid="waitlist-success"
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-[#4A7C7B]/30 bg-[#4A7C7B]/6 p-8 md:p-10"
      >
        <div className="mono text-[11px] tracking-[0.28em] uppercase text-[#4A7C7B]">Waitlist · confirmed</div>
        <div className="italic-serif text-3xl md:text-4xl mt-4 text-espresso leading-tight">
          You're on the list.
        </div>
        <p className="mt-4 text-charcoal/90 leading-relaxed max-w-lg">
          A quiet note will find you when Book One is ready. Between now and then, expect no more than one or two short updates.
        </p>
        <p className="mt-4 text-charcoal/80 text-sm leading-relaxed max-w-lg">
          Please check your inbox for a short welcome from Charlie sent to <span className="mono text-espresso">{email}</span>. If it doesn't appear within a few minutes, take a look in your spam folder and mark us as safe so future notes land well.
        </p>
        <ShareRow />
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="waitlist-form"
      className={compact ? "grid gap-4" : "grid gap-6"}
    >
      {!compact && (
        <label className="block">
          <span className="mono text-[10px] tracking-[0.28em] uppercase text-[#4A7C7B]">First name (optional)</span>
          <input
            data-testid="waitlist-first-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            type="text"
            className="mt-2 w-full bg-transparent border-b border-black/20 focus:border-[#4A7C7B] outline-none py-3 text-lg serif placeholder:italic placeholder:text-charcoal/40"
            placeholder="Sam"
          />
        </label>
      )}

      <label className="block">
        <span className="mono text-[10px] tracking-[0.28em] uppercase text-[#4A7C7B]">Your email</span>
        <input
          data-testid="waitlist-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          type="email"
          required
          className="mt-2 w-full bg-transparent border-b border-black/20 focus:border-[#4A7C7B] outline-none py-3 text-lg serif placeholder:italic placeholder:text-charcoal/40"
          placeholder="you@yourdomain.com"
        />
      </label>

      {!compact && (
        <label className="block">
          <span className="mono text-[10px] tracking-[0.28em] uppercase text-[#4A7C7B]">You are a…</span>
          <select
            data-testid="waitlist-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="mt-2 w-full bg-transparent border-b border-black/20 focus:border-[#4A7C7B] outline-none py-3 text-lg serif appearance-none pr-6"
          >
            {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </label>
      )}

      <div className="flex flex-wrap items-center gap-4 pt-2">
        <button
          type="submit"
          disabled={busy}
          data-testid="waitlist-submit"
          className="group inline-flex items-center gap-3 rounded-full bg-[#2C241B] text-[#FAF8F5] pl-6 pr-4 py-3.5 hover:bg-[#4A7C7B] transition-colors disabled:opacity-60"
        >
          {busy ? "Sending…" : "Join the waitlist"}
          <span className="w-8 h-8 grid place-items-center rounded-full bg-[#FAF8F5]/15 group-hover:bg-[#FAF8F5]/25 transition">
            <ArrowRight size={16} />
          </span>
        </button>
        <div className="text-xs text-charcoal/70 leading-relaxed">
          One careful email at launch. In between, no more than one or two short notes a month.
          <br />
          <span className="text-charcoal/55">No spam. No selling your email. Your email is used only for The Love Well Project.</span>
        </div>
      </div>
    </form>
  );
}
